import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { usePageMetadata } from '@srcDS/tastic/pages/category/hooks/usePageMetadata';
import { useCategoryPromotion } from '@srcDS/tastic/pages/category/hooks/useCategoryPromotion';
import { useCategoryFacets } from '@srcDS/tastic/pages/category/hooks/useCategoryFacets';

import PrimaryLoader from '@srcDS/components/atoms/primaryLoader/PrimaryLoader';
import { CategoryProvider } from '@srcDS/components/templates/category/context/CategoryContext';
import { CategorySchema } from '@srcDS/components/molecules/categorySchema';
import Category from '@srcDS/components/templates/category';

import withZendesk from '@srcDS/components/HOCs/withZendesk';
import categoryConnector from '@frontastic/catwalk/src/js/app/connector/category';
import urlHandlerConnector from '@frontastic/catwalk/src/js/app/connector/urlHandler';
import tastify from '@frontastic/catwalk/src/js/helper/tastify';
import { useHiddenPaginationLinks } from '@srcDS/tastic/pages/category/hooks/useHiddenPaginationLinks';

interface State {
  app: {
    route: {
      parameters: {
        s: any;
      };
    };
  };
}

interface CategoryContentTasticProps {
  data: any;
  context: any;
  node: any;
  route: any;
  tastic: any;
  urlHandler?: any;
  categories?: any;
  facets?: any;
  isServerSideRendering?: boolean;
}

export const CategoryContentTastic: React.FC<CategoryContentTasticProps> = ({
  data,
  context,
  node,
  route,
  tastic,
  urlHandler = null,
  categories,
  isServerSideRendering,
}) => {
  const { locale } = usePageMetadata(context, node);
  const { filteredFacets } = useCategoryFacets(data);
  const { hiddenPaginateLinks } = useHiddenPaginationLinks(node, route, tastic, urlHandler, data.stream, context);

  const stream = data?.stream;
  const isSSR = typeof isServerSideRendering === 'boolean' && isServerSideRendering;
  const categoryUrl = typeof window !== 'undefined' ? window.location.pathname : '';
  const isSearchPage = node.nodeType === 'search';

  const { categoryData } = useCategoryPromotion(data, node, isSearchPage);

  if (!urlHandler) return null;
  if (!data[`show${locale}`]) return null;
  if (!data) return <PrimaryLoader mode="globalInner" />;

  const categorySchema = useMemo(
    () => (
      <CategorySchema
        url={categoryUrl}
        name={stream.projectSpecificData?.metaTitle || data.categoryTitle}
        description={stream.projectSpecificData?.metaDescription || data.categoryText}
        metaCategoryName={stream.projectSpecificData?.name}
        products={stream.items}
        node={node}
      />
    ),
    [node, categoryUrl, data.categoryTitle, data.categoryText, stream.items],
  );

  const staticObjects = {
    context,
    node,
    route,
    tastic,
    urlHandler,
    categories,
    facets: filteredFacets,
    isSSR,
  };


  return (
    <>
      {categorySchema}
      <CategoryProvider data={categoryData} objects={staticObjects}>
        <Category />
      </CategoryProvider>
      {hiddenPaginateLinks}
    </>
  );
};

export default tastify({
  translate: true,
  connect: {
    context: true,
    node: true,
    tastic: true,
    route: true,
    page: true,
    wishlist: true,
    urlHandler: true,
    isServerSideRendering: true,
  },
})(
  compose(
    withZendesk,
    connect(categoryConnector),
    connect(urlHandlerConnector),
    // @ts-ignore
    connect((state: State) => {
      const streamParameters = state.app.route.parameters.s || {};

      return {
        route: state.app.route,
        streamParameters,
      };
    }),
  )(CategoryContentTastic),
);
